import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import dayjs from 'dayjs'
import 'dayjs/locale/uk'
import 'dayjs/locale/ru'
import 'dayjs/locale/fr'
import 'dayjs/locale/de'
import 'dayjs/locale/it'
import 'dayjs/locale/es'
import 'dayjs/locale/pl'
import 'dayjs/locale/nb'
import 'dayjs/locale/nn'
import 'dayjs/locale/sv'
import 'dayjs/locale/fi'
import 'dayjs/locale/ms'
import 'dayjs/locale/sq'
import 'dayjs/locale/pt-br'
import 'dayjs/locale/se'
import 'dayjs/locale/is'
import 'dayjs/locale/lt'
import 'dayjs/locale/nl'
import 'dayjs/locale/ja'
import 'dayjs/locale/ro'
import 'dayjs/locale/da'
import 'dayjs/locale/hu'
import 'dayjs/locale/et'
import 'dayjs/locale/lv'
import 'dayjs/locale/zh'
import 'dayjs/locale/el'
import 'dayjs/locale/pt'

interface FormatPeriodOptions {
    dateFormat?: string
    presentText?: string
    invalidDateText?: string
    rangeSeparator?: string
    lang?: string
    startYear?: string | null
    endYear?: string | null
    startMonth?: string | null
    endMonth?: string | null
    startDay?: string | null
    endDay?: string | null
    isShowPresent?: boolean
    fromDate?: string | null
    toDate?: string | null
}

interface FormatDurationOptions {
    showMonths?: boolean
    invalidDateText?: string
    lang?: string
    startYear?: string | null
    endYear?: string | null
    startMonth?: string | null
    endMonth?: string | null
    startDay?: string | null
    endDay?: string | null
    fromDate?: string | null
    toDate?: string | null
}

export function useFormattedDatesPeriod() {
    const { getTitle } = useApplyV2NavTranslations()

    const pluralRules: Record<string, Record<string, string[]>> = {
        uk: {
            year: ['рік', 'роки', 'років'],
            month: ['місяць', 'місяці', 'місяців'],
        },
        ru: {
            year: ['год', 'года', 'лет'],
            month: ['месяц', 'месяца', 'месяцев'],
        },
    }

    const getPluralForm = (count: number, unit: string, lang: string): string => {
        if (!pluralRules[lang]?.[unit]) {
            // return count !== 1 ? `${unit}s` : unit
            return unit
        }

        const forms = pluralRules[lang][unit]
        const lastTwo = count % 100
        const lastOne = count % 10

        if (lang === 'uk' || lang === 'ru') {
            if (lastTwo >= 11 && lastTwo <= 19) return forms[2]
            if (lastOne === 1) return forms[0]
            if (lastOne >= 2 && lastOne <= 4) return forms[1]
            return forms[2]
        }

        return count !== 1 ? forms[1] : forms[0]
    }

    return {
        formatDateRange: (
            fromDat?: string | number | null,
            toDat?: string | number | null,
            options?: FormatPeriodOptions
        ): string => {
            const {
                dateFormat = 'MMM YYYY',
                presentText = getTitle('1226', 'Present'),
                invalidDateText = getTitle('148', 'Invalid date'),
                rangeSeparator = ' – ',
                lang = document?.documentElement?.lang ?? 'en',
                isShowPresent = true,
                startYear = '',
                endYear = '',
                startMonth = '',
                endMonth = '',
                startDay = '',
                endDay = '',
                fromDate = fromDat || '',
                toDate = !toDat || toDat !== '0000-00-00' ? toDat : '',
            } = options || {}

            dayjs.locale(lang)

            if (
                !fromDate &&
                !toDate &&
                !startYear &&
                !endYear &&
                !endYear &&
                !startMonth &&
                !endMonth &&
                !startDay &&
                !endDay
            ) {
                return ''
            }

            try {
                if (fromDate || toDate) {
                    const from = fromDate ? dayjs(fromDate) : null
                    const to = toDate && toDate !== '0000-00-00' ? dayjs(toDate) : isShowPresent ? dayjs() : null

                    const fromFormatted = from?.format(dateFormat) ?? ''
                    let toFormatted = ''

                    if (toDate && toDate !== '0000-00-00') {
                        toFormatted = dayjs(toDate).format(dateFormat)
                    } else if (isShowPresent) {
                        toFormatted = presentText
                    }

                    return [fromFormatted, toFormatted].filter(Boolean).join(rangeSeparator)
                }
                if (startYear || startMonth || startDay || endYear || endMonth || endDay) {
                    const from =
                        startYear || startMonth || startDay ? dayjs(`${startYear}-${startMonth}-${startDay}`) : null
                    const to =
                        endYear || endMonth || endDay
                            ? dayjs(`${endYear}-${endMonth}-${endDay}`)
                            : isShowPresent
                              ? dayjs()
                              : null

                    const fromFormatted = from?.format(dateFormat) ?? ''
                    let toFormatted = ''

                    if (endYear || endMonth || endDay) {
                        toFormatted = dayjs(`${endYear}-${endMonth}-${endDay}`).format(dateFormat)
                    } else if (isShowPresent) {
                        toFormatted = presentText
                    }

                    return [fromFormatted, toFormatted].filter(Boolean).join(rangeSeparator)
                }
                return ''
            } catch (error) {
                console.error('Error formatting date range:', error)
                return options?.invalidDateText || getTitle('148', 'Invalid date')
            }
        },

        formatDuration: (
            fromDat?: string | number | null,
            toDat?: string | number | null,
            options?: FormatDurationOptions
        ): string => {
            const {
                showMonths = true,
                invalidDateText = getTitle('148', 'Invalid date'),
                lang = document?.documentElement?.lang ?? 'en',
                startYear = '',
                endYear = '',
                startMonth = '',
                endMonth = '',
                startDay = '',
                endDay = '',
                fromDate = fromDat || '',
                toDate = !toDat || toDat !== '0000-00-00' ? toDat : '',
            } = options || {}

            dayjs.locale(lang)

            if (!fromDate && !startYear) return ''

            try {
                if (fromDate || toDate) {
                    const from = dayjs(fromDate)
                    const to = toDate && toDate !== '0000-00-00' ? dayjs(toDate) : dayjs()

                    const years = to.diff(from, 'year')
                    const months = to.diff(from.add(years, 'year'), 'month')

                    const durationParts = []
                    if (years > 0) {
                        durationParts.push(
                            `${years} ${years !== 1 ? getTitle('3120', 'Years') : getTitle('1322', 'Year')}`
                        )
                    }
                    if (showMonths && months > 0) {
                        durationParts.push(
                            `${months} ${months !== 1 ? getTitle('2763', 'Months') : getTitle('931', 'Month')}`
                        )
                    }
                    return durationParts.join(' ').toLowerCase()
                }
                if (startYear || endYear) {
                    const from = dayjs(`${startYear}-${startMonth}-${startDay}`)
                    const to = endYear || endMonth || endDay ? dayjs(`${endYear}-${endMonth}-${endDay}`) : dayjs()

                    const years = to.diff(from, 'year')
                    const months = to.diff(from.add(years, 'year'), 'month')

                    const durationParts = []
                    if (years > 0) {
                        durationParts.push(
                            `${years} ${years !== 1 ? getTitle('3120', 'Years') : getTitle('1322', 'Year')}`
                        )
                    }
                    if (showMonths && months > 0) {
                        durationParts.push(
                            `${months} ${months !== 1 ? getTitle('2763', 'Months') : getTitle('931', 'Month')}`
                        )
                    }
                    return durationParts.join(' ').toLowerCase()
                }
                return ''
            } catch (error) {
                console.error('Error formatting duration:', error)
                return invalidDateText
            }
        },
    }
}

export default useFormattedDatesPeriod
